/* ==========================================================================
   Hadara — dynamic terms & conditions engine.

   Contains NO Liquid, by the same rule as quote-page.js: Shopify must never
   parse this file. Everything configurable arrives through the JSON block the
   section renders, or lives in the TEMPLATES table below.

   Responsibilities
     - hold the three supplied templates plus a free-form custom mode
     - render an editor: edit text, add/remove list items and payment stages,
       show/hide sections, reorder, add custom clauses
     - save per quotation, keyed on the quote reference
     - hand a structured document to the PDF writer, the email body and the
       printable page

   Public API (window.HadaraTerms)
     mount(root, cfg, reference)  build the editor inside [data-terms-app]
     doc()                        current structured document
     flat()                       array of plain strings, one per clause
     text()                       plain-text block for the email body
     load(reference)              read a saved document without mounting
   ========================================================================== */
(function () {
  'use strict';

  var STORE_PREFIX = 'hadara-terms-v1:';
  var NBSP_DASH = '\u2013';           /* en dash, as used in "2-4 weeks" */
  var RSQUO = '\u2019';

  /* ------------------------------------------------------------------ *
   * Templates                                                          *
   * Edit these to change the master wording for the whole store.        *
   * ------------------------------------------------------------------ */
  function baseDelivery(period) {
    return {
      id: 'delivery',
      title: 'Delivery',
      kind: 'list',
      period: period,
      intro: 'Delivery is scheduled within {period}, subject to:',
      items: [
        'Issuance of the LPO',
        'Completion of agreed payments',
        'Finalization of indoor and outdoor MEP works at site'
      ],
      show: true
    };
  }

  function baseScope() {
    return {
      id: 'scope',
      title: 'Scope of Work',
      kind: 'list',
      intro: '',
      items: ['Site meetings', 'Testing and commissioning', 'Supervision'],
      show: true
    };
  }

  var NOTES_TEXT = 'All prices are in AED. BOQ quantities are approximate and may be ' +
    'adjusted according to actual site requirements and installation conditions.';

  var TEMPLATES = {
    standard: {
      id: 'standard',
      name: 'Standard Supply & Installation',
      short: 'Template 1',
      sections: function () {
        return [
          {
            id: 'exclusions', title: 'Exclusions', kind: 'text', show: true,
            body: 'Additional items or services not specifically mentioned in this quotation, ' +
              'civil works, DU or Etisalat internet router, all types of cable pulling, lighting, ' +
              '230VAC power supply and cabling for curtain motors, AC cabling infrastructure, and ' +
              'electrical neutral cables.'
          },
          {
            id: 'warranty', title: 'Warranty', kind: 'warranty', show: true,
            duration: '1-Year', body: 'Manufacturer' + RSQUO + 's Warranty.'
          },
          baseDelivery('2' + NBSP_DASH + '4 weeks'),
          baseScope(),
          {
            id: 'payment', title: 'Payment Terms', kind: 'stages', show: true,
            stages: [
              { pct: 50, label: 'advance payment' },
              { pct: 20, label: 'after completion of cable pulling / MEP scope of work' },
              { pct: 25, label: 'on the same day of material delivery' },
              { pct: 5, label: 'after testing and commissioning' }
            ]
          },
          { id: 'notes', title: 'Additional Notes', kind: 'text', show: true, body: NOTES_TEXT }
        ];
      }
    },

    supply: {
      id: 'supply',
      name: 'Supply Only',
      short: 'Template 2',
      sections: function () {
        return [
          {
            id: 'exclusions', title: 'Exclusions', kind: 'text', show: true,
            body: 'This offer excludes any additional items, works, or services not specifically ' +
              'mentioned in the quotation.'
          },
          {
            id: 'warranty', title: 'Warranty', kind: 'warranty', show: true,
            duration: '1-Year', body: 'Manufacturer' + RSQUO + 's Warranty.'
          },
          {
            id: 'delivery', title: 'Delivery', kind: 'list', show: true,
            period: '3' + NBSP_DASH + '4 weeks',
            intro: 'Delivery will be scheduled within {period} from issuance of the LPO and ' +
              'receipt of payment according to the agreed payment terms.',
            items: []
          },
          {
            id: 'scope', title: 'Scope of Work', kind: 'list', show: true,
            intro: 'Supply and delivery only.', items: []
          },
          {
            id: 'payment', title: 'Payment Terms', kind: 'stages', show: true,
            stages: [{ pct: 100, label: 'advance payment' }]
          },
          { id: 'notes', title: 'Additional Notes', kind: 'text', show: false, body: NOTES_TEXT }
        ];
      }
    },

    premium: {
      id: 'premium',
      name: 'Premium Supply & Installation',
      short: 'Template 3',
      sections: function () {
        return [
          {
            id: 'exclusions', title: 'Exclusions', kind: 'text', show: true,
            body: 'Fabrication and fixing, additional items, civil works, all types of cable ' +
              'pulling, lighting, 230VAC power supply and cabling for curtain motors, and ' +
              'electrical neutral cables.'
          },
          {
            id: 'warranty', title: 'Warranty', kind: 'warranty', show: true,
            duration: '5-Year', body: 'Manufacturer' + RSQUO + 's Warranty.'
          },
          baseDelivery('2' + NBSP_DASH + '4 weeks'),
          baseScope(),
          {
            id: 'payment', title: 'Payment Terms', kind: 'stages', show: true,
            stages: [
              { pct: 60, label: 'advance payment' },
              { pct: 30, label: 'upon material delivery' },
              { pct: 10, label: 'after testing and commissioning' }
            ]
          },
          { id: 'notes', title: 'Additional Notes', kind: 'text', show: true, body: NOTES_TEXT }
        ];
      }
    }
  };

  var ORDER = ['standard', 'supply', 'premium'];

  /* ------------------------------------------------------------------ *
   * State                                                              *
   * ------------------------------------------------------------------ */
  var state = null;      /* the live document */
  var opts = {};         /* config from the section */
  var host = null;       /* editor container */
  var refKey = '';
  var saveTimer = null;
  var onChange = null;

  function uid() {
    return 's' + Math.random().toString(36).slice(2, 9);
  }

  function clone(v) {
    return JSON.parse(JSON.stringify(v));
  }

  function buildDoc(templateId) {
    var t = TEMPLATES[templateId] || TEMPLATES.standard;
    var secs = t.sections();
    secs.forEach(function (s) { s.key = uid(); s.custom = false; });
    return {
      version: 1,
      template: t.id,
      edited: false,
      title: opts.title || 'Terms & Conditions',
      sections: secs,
      bank: clone(opts.bank || {})
    };
  }

  /* ------------------------------------------------------------------ *
   * Persistence — one saved document per quotation reference           *
   * ------------------------------------------------------------------ */
  function keyFor(ref) { return STORE_PREFIX + (ref || 'current'); }

  function save() {
    if (!state) return;
    try { localStorage.setItem(keyFor(refKey), JSON.stringify(state)); } catch (e) {}
    flagSaved();
  }

  function load(ref) {
    try {
      var raw = localStorage.getItem(keyFor(ref));
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || !d.sections) return null;
      d.sections.forEach(function (s) { if (!s.key) s.key = uid(); });
      if (!d.bank) d.bank = clone(opts.bank || {});
      return d;
    } catch (e) { return null; }
  }

  function queueSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 400);
  }

  function touched() {
    if (state) state.edited = true;
    queueSave();
    stampTemplateLabel();
    if (typeof onChange === 'function') onChange();
  }

  var savedNote = null;
  function flagSaved() {
    if (!savedNote) return;
    savedNote.textContent = 'Saved to this quotation';
    savedNote.classList.add('is-on');
    clearTimeout(flagSaved._t);
    flagSaved._t = setTimeout(function () { savedNote.classList.remove('is-on'); }, 2200);
  }

  /* ------------------------------------------------------------------ *
   * Rendering the editor                                               *
   * ------------------------------------------------------------------ */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }

  /* A textarea's scrollHeight never falls below its rows attribute, so rows is
     pinned to 1 and the real content height is measured from there. */
  function autosize(ta) {
    ta.rows = 1;
    ta.style.height = 'auto';
    ta.style.height = Math.max(38, ta.scrollHeight + 2) + 'px';
  }

  function field(value, placeholder, onInput, rows) {
    var ta = el('textarea', 'rfq-tc__ta');
    ta.rows = rows || 2;
    ta.value = value || '';
    if (placeholder) ta.placeholder = placeholder;
    ta.addEventListener('input', function () {
      autosize(ta);
      onInput(ta.value);
      touched();
    });
    return ta;
  }

  function line(value, placeholder, onInput) {
    var i = el('input', 'rfq-tc__in');
    i.type = 'text';
    i.value = value || '';
    if (placeholder) i.placeholder = placeholder;
    i.addEventListener('input', function () { onInput(i.value); touched(); });
    return i;
  }

  function iconBtn(label, title, cls) {
    var b = el('button', 'rfq-tc__icon' + (cls ? ' ' + cls : ''), label);
    b.type = 'button';
    b.title = title;
    b.setAttribute('aria-label', title);
    return b;
  }

  var tplLabel = null;
  function stampTemplateLabel() {
    if (!tplLabel || !state) return;
    var t = TEMPLATES[state.template];
    tplLabel.textContent = (t ? t.short + ' \u2014 ' + t.name : 'Custom terms') +
      (state.edited ? ' \u00b7 edited' : '');
  }

  function render() {
    if (!host || !state) return;
    host.innerHTML = '';

    /* ---- template chooser ---- */
    var head = el('div', 'rfq-tc__head');
    var title = el('span', 'rfq-tc__title', state.title || 'Terms & Conditions');
    head.appendChild(title);
    tplLabel = el('span', 'rfq-tc__tpl-label');
    head.appendChild(tplLabel);
    savedNote = el('span', 'rfq-tc__saved', 'Saved to this quotation');
    head.appendChild(savedNote);
    host.appendChild(head);

    var pills = el('div', 'rfq-tc__pills');
    ORDER.forEach(function (id) {
      var t = TEMPLATES[id];
      var b = el('button', 'rfq-tc__pill' + (state.template === id ? ' is-on' : ''));
      b.type = 'button';
      b.innerHTML = '<b>' + t.short + '</b><span>' + t.name + '</span>';
      b.addEventListener('click', function () { applyTemplate(id); });
      pills.appendChild(b);
    });
    var custom = el('button', 'rfq-tc__pill' + (state.template === 'custom' ? ' is-on' : ''));
    custom.type = 'button';
    custom.innerHTML = '<b>Custom</b><span>Start from what is on screen</span>';
    custom.addEventListener('click', function () {
      state.template = 'custom';
      touched();
      render();
    });
    pills.appendChild(custom);
    host.appendChild(pills);

    /* ---- sections ---- */
    var list = el('div', 'rfq-tc__list');
    state.sections.forEach(function (sec, idx) {
      list.appendChild(sectionCard(sec, idx));
    });
    host.appendChild(list);

    /* ---- bank details ---- */
    host.appendChild(bankCard());

    /* ---- footer actions ---- */
    var foot = el('div', 'rfq-tc__foot');

    var add = el('button', 'rfq-tc__add', '+  Add clause');
    add.type = 'button';
    add.addEventListener('click', function () {
      state.sections.push({
        key: uid(), id: 'custom-' + uid(), title: 'New clause',
        kind: 'text', body: '', show: true, custom: true
      });
      state.template = 'custom';
      touched();
      render();
      var cards = host.querySelectorAll('.rfq-tc__card');
      var last = cards[cards.length - 1];
      if (last) {
        last.scrollIntoView({ block: 'center', behavior: 'smooth' });
        var t = last.querySelector('textarea, input');
        if (t) t.focus();
      }
    });
    foot.appendChild(add);

    var saveBtn = el('button', 'rfq-tc__add rfq-tc__add--solid', 'Save terms');
    saveBtn.type = 'button';
    saveBtn.addEventListener('click', function () { save(); });
    foot.appendChild(saveBtn);

    var reset = el('button', 'rfq-tc__add rfq-tc__add--quiet', 'Reset to template');
    reset.type = 'button';
    reset.addEventListener('click', function () {
      var id = state.template === 'custom' ? (opts.defaultTemplate || 'standard') : state.template;
      if (!confirm('Reset every clause back to ' + (TEMPLATES[id] ? TEMPLATES[id].short : 'the default template') +
                   '? Edits made on this quotation will be lost.')) return;
      state = buildDoc(id);
      save();
      render();
      if (typeof onChange === 'function') onChange();
    });
    foot.appendChild(reset);

    host.appendChild(foot);
    stampTemplateLabel();

    /* Height has to be measured after the browser has laid the cards out,
       otherwise scrollHeight is read against an unstyled textarea and every
       box comes back several lines too tall. */
    var fit = function () {
      var tas = host.querySelectorAll('.rfq-tc__ta');
      for (var i = 0; i < tas.length; i++) autosize(tas[i]);
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(fit);
    else setTimeout(fit, 0);
  }

  function applyTemplate(id) {
    if (state.edited && !confirm('Load ' + TEMPLATES[id].short + '? Edits made on this quotation will be replaced.')) return;
    var bank = clone(state.bank);
    state = buildDoc(id);
    state.bank = bank;
    save();
    render();
    if (typeof onChange === 'function') onChange();
  }

  function sectionCard(sec, idx) {
    var card = el('div', 'rfq-tc__card' + (sec.show ? '' : ' is-off'));

    var bar = el('div', 'rfq-tc__bar');

    var tin = el('input', 'rfq-tc__heading');
    tin.type = 'text';
    tin.value = sec.title || '';
    tin.placeholder = 'Clause heading';
    tin.addEventListener('input', function () { sec.title = tin.value; touched(); });
    bar.appendChild(tin);

    var tools = el('div', 'rfq-tc__tools');

    var eye = iconBtn(sec.show ? 'Shown' : 'Hidden', sec.show ? 'Hide this section' : 'Show this section',
                      'rfq-tc__icon--toggle' + (sec.show ? ' is-on' : ''));
    eye.addEventListener('click', function () {
      sec.show = !sec.show;
      touched();
      render();
    });
    tools.appendChild(eye);

    var up = iconBtn('\u2191', 'Move up');
    up.disabled = idx === 0;
    up.addEventListener('click', function () { move(idx, -1); });
    tools.appendChild(up);

    var down = iconBtn('\u2193', 'Move down');
    down.disabled = idx === state.sections.length - 1;
    down.addEventListener('click', function () { move(idx, 1); });
    tools.appendChild(down);

    var del = iconBtn('\u00d7', 'Remove this section', 'rfq-tc__icon--del');
    del.addEventListener('click', function () {
      if (!confirm('Remove "' + (sec.title || 'this clause') + '" from this quotation?')) return;
      state.sections.splice(idx, 1);
      state.template = 'custom';
      touched();
      render();
    });
    tools.appendChild(del);

    bar.appendChild(tools);
    card.appendChild(bar);

    var body = el('div', 'rfq-tc__body');

    if (sec.kind === 'warranty') {
      var wrow = el('div', 'rfq-tc__row');
      var wl = el('label', 'rfq-tc__lbl', 'Duration');
      var wd = line(sec.duration, 'e.g. 5-Year', function (v) { sec.duration = v; });
      wd.className = 'rfq-tc__in rfq-tc__in--short';
      wl.appendChild(wd);
      wrow.appendChild(wl);
      var w2 = el('label', 'rfq-tc__lbl rfq-tc__lbl--grow', 'Wording');
      w2.appendChild(line(sec.body, 'Manufacturer' + RSQUO + 's Warranty.', function (v) { sec.body = v; }));
      wrow.appendChild(w2);
      body.appendChild(wrow);
      var prev = el('p', 'rfq-tc__preview');
      prev.textContent = composeWarranty(sec);
      body.appendChild(prev);

    } else if (sec.kind === 'list') {
      if (sec.id === 'delivery' || sec.period !== undefined) {
        var prow = el('label', 'rfq-tc__lbl', 'Delivery period');
        var pin = line(sec.period, 'e.g. 2' + NBSP_DASH + '4 weeks', function (v) { sec.period = v; });
        pin.className = 'rfq-tc__in rfq-tc__in--short';
        prow.appendChild(pin);
        body.appendChild(prow);
      }
      var introHint = sec.period !== undefined
        ? 'Opening sentence \u2014 use {period} where the delivery time should appear'
        : 'Opening sentence (optional)';
      body.appendChild(field(sec.intro, introHint, function (v) { sec.intro = v; }, 2));
      body.appendChild(itemsEditor(sec, 'Add item'));

    } else if (sec.kind === 'stages') {
      body.appendChild(stagesEditor(sec));

    } else {
      body.appendChild(field(sec.body, 'Clause text', function (v) { sec.body = v; }, 3));
    }

    card.appendChild(body);
    return card;
  }

  function move(idx, dir) {
    var to = idx + dir;
    if (to < 0 || to >= state.sections.length) return;
    var tmp = state.sections[idx];
    state.sections[idx] = state.sections[to];
    state.sections[to] = tmp;
    touched();
    render();
  }

  function itemsEditor(sec, addLabel) {
    var wrap = el('div', 'rfq-tc__items');
    (sec.items || []).forEach(function (item, i) {
      var row = el('div', 'rfq-tc__item');
      row.appendChild(el('span', 'rfq-tc__bullet', '\u2022'));
      row.appendChild(line(item, 'Item', function (v) { sec.items[i] = v; }));
      var x = iconBtn('\u00d7', 'Remove item', 'rfq-tc__icon--del');
      x.addEventListener('click', function () {
        sec.items.splice(i, 1);
        touched();
        render();
      });
      row.appendChild(x);
      wrap.appendChild(row);
    });
    var add = el('button', 'rfq-tc__mini', '+ ' + addLabel);
    add.type = 'button';
    add.addEventListener('click', function () {
      if (!sec.items) sec.items = [];
      sec.items.push('');
      touched();
      render();
      var rows = wrap.parentNode.querySelectorAll('.rfq-tc__item input');
      if (rows.length) rows[rows.length - 1].focus();
    });
    wrap.appendChild(add);
    return wrap;
  }

  function stagesEditor(sec) {
    var wrap = el('div', 'rfq-tc__items');
    var total = 0;
    (sec.stages || []).forEach(function (st, i) {
      total += Number(st.pct) || 0;
      var row = el('div', 'rfq-tc__item rfq-tc__item--stage');
      var pct = el('input', 'rfq-tc__pct');
      pct.type = 'number';
      pct.min = '0';
      pct.max = '100';
      pct.step = '1';
      pct.value = st.pct;
      pct.setAttribute('aria-label', 'Percentage for stage ' + (i + 1));
      pct.addEventListener('input', function () {
        st.pct = pct.value === '' ? '' : Number(pct.value);
        touched();
        updateTotal();
      });
      row.appendChild(pct);
      row.appendChild(el('span', 'rfq-tc__pctsign', '%'));
      row.appendChild(line(st.label, 'e.g. advance payment', function (v) { st.label = v; }));
      var x = iconBtn('\u00d7', 'Remove stage', 'rfq-tc__icon--del');
      x.addEventListener('click', function () {
        sec.stages.splice(i, 1);
        touched();
        render();
      });
      row.appendChild(x);
      wrap.appendChild(row);
    });

    var bar = el('div', 'rfq-tc__stagefoot');
    var add = el('button', 'rfq-tc__mini', '+ Add stage');
    add.type = 'button';
    add.addEventListener('click', function () {
      if (!sec.stages) sec.stages = [];
      sec.stages.push({ pct: 0, label: '' });
      touched();
      render();
    });
    bar.appendChild(add);

    var badge = el('span', 'rfq-tc__total');
    bar.appendChild(badge);
    wrap.appendChild(bar);

    function updateTotal() {
      var t = 0;
      (sec.stages || []).forEach(function (s) { t += Number(s.pct) || 0; });
      badge.textContent = 'Total ' + t + '%';
      badge.className = 'rfq-tc__total' + (t === 100 ? ' is-ok' : ' is-warn');
      badge.title = t === 100 ? 'Milestones add up to 100%' : 'Milestones do not add up to 100%';
    }
    updateTotal();
    return wrap;
  }

  function bankCard() {
    var b = state.bank || (state.bank = {});
    var card = el('div', 'rfq-tc__card rfq-tc__card--bank' + (b.show ? '' : ' is-off'));

    var bar = el('div', 'rfq-tc__bar');
    bar.appendChild(el('span', 'rfq-tc__heading rfq-tc__heading--static', b.title || 'Bank Details for Payment'));
    var tools = el('div', 'rfq-tc__tools');
    var eye = iconBtn(b.show ? 'Shown' : 'Hidden', b.show ? 'Hide bank details' : 'Show bank details',
                      'rfq-tc__icon--toggle' + (b.show ? ' is-on' : ''));
    eye.addEventListener('click', function () { b.show = !b.show; touched(); render(); });
    tools.appendChild(eye);
    bar.appendChild(tools);
    card.appendChild(bar);

    var body = el('div', 'rfq-tc__body');
    [
      ['holder', 'Account holder name'],
      ['bank', 'Bank name'],
      ['account', 'Account number'],
      ['iban', 'IBAN']
    ].forEach(function (pair) {
      var l = el('label', 'rfq-tc__lbl rfq-tc__lbl--grow', pair[1]);
      l.appendChild(line(b[pair[0]], pair[1], function (v) { b[pair[0]] = v; }));
      body.appendChild(l);
    });
    card.appendChild(body);
    return card;
  }

  /* ------------------------------------------------------------------ *
   * Output                                                             *
   * ------------------------------------------------------------------ */
  function composeWarranty(sec) {
    var d = (sec.duration || '').trim();
    var t = (sec.body || '').trim();
    if (d && t) return d + ' ' + t;
    return d || t;
  }

  function introText(sec) {
    var s = (sec.intro || '').trim();
    if (!s) return '';
    return s.replace(/\{period\}/g, (sec.period || '').trim());
  }

  function stageText(st) {
    var p = (st.pct === '' || st.pct === null || st.pct === undefined) ? '' : String(st.pct) + '%';
    var l = (st.label || '').trim();
    return [p, l].filter(Boolean).join(' ');
  }

  /* Structured document for the PDF writer and the printable page. Each
     block is { title, lines[], bullets[] } so renderers stay simple. */
  function compose(src) {
    var state = src;
    if (!state || !state.sections) return null;
    var out = { title: state.title || 'Terms & Conditions', template: state.template, blocks: [], bank: null };

    state.sections.forEach(function (sec) {
      if (!sec.show) return;
      var blk = { title: (sec.title || '').trim(), lines: [], bullets: [] };

      if (sec.kind === 'warranty') {
        var w = composeWarranty(sec);
        if (w) blk.lines.push(w);
      } else if (sec.kind === 'list') {
        var it = introText(sec);
        if (it) blk.lines.push(it);
        (sec.items || []).forEach(function (i) {
          i = (i || '').trim();
          if (i) blk.bullets.push(i);
        });
      } else if (sec.kind === 'stages') {
        (sec.stages || []).forEach(function (st) {
          var s = stageText(st);
          if (s) blk.bullets.push(s);
        });
      } else {
        var b = (sec.body || '').trim();
        if (b) blk.lines.push(b);
      }

      if (blk.lines.length || blk.bullets.length) out.blocks.push(blk);
    });

    var bk = state.bank || {};
    if (bk.show) {
      out.bank = {
        title: bk.title || 'Bank Details for Payment',
        rows: [
          ['Account Holder Name', bk.holder],
          ['Bank Name', bk.bank],
          ['Account Number', bk.account],
          ['IBAN', bk.iban]
        ].filter(function (r) { return (r[1] || '').toString().trim(); })
      };
      if (!out.bank.rows.length) out.bank = null;
    }
    return out;
  }

  function doc() { return compose(state); }

  /* One clause per string, for renderers that only understand a flat list. */
  function flat(src) {
    var d = src ? compose(src) : doc();
    if (!d) return [];
    var out = [];
    d.blocks.forEach(function (b) {
      var parts = b.lines.slice();
      b.bullets.forEach(function (x) { parts.push('\u2022 ' + x); });
      out.push((b.title ? b.title + ': ' : '') + parts.join(' '));
    });
    if (d.bank) {
      out.push(d.bank.title + ': ' + d.bank.rows.map(function (r) { return r[0] + ' ' + r[1]; }).join(', '));
    }
    return out;
  }

  /* Plain text for the email body. */
  function text(src) {
    var d = src ? compose(src) : doc();
    if (!d) return '';
    var L = [d.title.toUpperCase()];
    d.blocks.forEach(function (b) {
      L.push('');
      if (b.title) L.push(b.title);
      b.lines.forEach(function (x) { L.push(x); });
      b.bullets.forEach(function (x) { L.push('  - ' + x); });
    });
    if (d.bank) {
      L.push('');
      L.push(d.bank.title.toUpperCase());
      d.bank.rows.forEach(function (r) { L.push(r[0] + ': ' + r[1]); });
    }
    return L.join('\n');
  }

  /* ------------------------------------------------------------------ *
   * Mount                                                              *
   * ------------------------------------------------------------------ */
  function mount(root, cfg, reference, changed) {
    opts = cfg || {};
    refKey = reference || 'current';
    onChange = changed || null;
    host = root.querySelector('[data-terms-app]');

    state = load(refKey);
    if (!state) {
      /* First view of this quotation: build from the admin default and store it
         straight away, so the printable page and the PDF have something to read
         even if nothing is edited. */
      state = buildDoc(opts.defaultTemplate || 'standard');
      save();
    } else if (opts.bank && state.bank && state.bank.show === undefined) {
      state.bank.show = !!opts.bank.show;
    }

    if (host) render();
    return { doc: doc, flat: flat, text: text };
  }

  window.HadaraTerms = {
    mount: mount,
    compose: compose,
    doc: doc,
    flat: flat,
    text: text,
    load: load,
    templates: TEMPLATES
  };
})();
